import { useTranslation } from "react-i18next";
import { ITEM_KINDS } from "../../../api/content/reviewDecoders";
import Field from "../../../ui/forms/Field";
import { classNames } from "../../../ui/styles/classNames";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import SearchQueryField, { type ComboboxWiring } from "../query/SearchQueryField";
import type { LibraryFilter, LibrarySearch } from "./useLibrarySearch";
import { itemKindText } from "../../workflows/inputCards/typeWords";

interface LibrarySearchControlsProps {
  search: LibrarySearch;
  /** What the field searches, as its placeholder. */
  label: string;
  /** Set where the field drives a result list (`LibraryPicker`). */
  combobox?: ComboboxWiring;
}

/** The library search's query and its filter by kind or artifact type. The library search has fuzzy words in every mode. */
export default function LibrarySearchControls({ search, label, combobox }: LibrarySearchControlsProps) {
  const { t } = useTranslation();
  const offersKinds = search.kinds.length > 1;
  const offersTypes = search.contentTypes.length > 0;
  return (
    <div className="space-y-2">
      <SearchQueryField query={search.query} onQueryChange={search.setQuery} fuzzy="available" label={label} combobox={combobox} />
      {offersKinds || offersTypes ? (
        <Field label={t("search.library.filter")} layout="inline" className="text-xs">
          {(control) => (
            <select
              {...control}
              className={classNames(FORM_SELECT_CLASS, "w-auto text-xs")}
              value={filterValue(search.filter)}
              onChange={(event) => { search.setFilter(parseFilter(event.target.value)); }}
            >
              <option value="">{t("search.library.everything")}</option>
              {offersKinds ? (
                <optgroup label={t("search.library.kinds")}>
                  {search.kinds.map((kind) => <option key={kind} value={`kind:${kind}`}>{itemKindText(kind, t)}</option>)}
                </optgroup>
              ) : null}
              {offersTypes ? (
                <optgroup label={t("search.library.artifactTypes")}>
                  {search.contentTypes.map((contentType) => <option key={contentType} value={`type:${contentType}`}>{contentType}</option>)}
                </optgroup>
              ) : null}
            </select>
          )}
        </Field>
      ) : null}
    </div>
  );
}

function filterValue(filter: LibraryFilter | null): string {
  if (filter === null) return "";
  return filter.by === "kind" ? `kind:${filter.kind}` : `type:${filter.contentType}`;
}

function parseFilter(value: string): LibraryFilter | null {
  if (value.startsWith("type:")) return { by: "contentType", contentType: value.slice("type:".length) };
  const kind = ITEM_KINDS.find((candidate) => value === `kind:${candidate}`);
  return kind === undefined ? null : { by: "kind", kind };
}
